import React, { useState } from 'react';
import { X, FileText, Edit, Eye, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../services/api';

interface CreateManifestoModalProps {
  aspirantId: number;
  onClose: () => void;
  onSuccess: () => void;
}

type ManifestoFormData = {
  title: string;
  content: string;
  summary: string;
  visionStatement: string;
  missionStatement: string;
  source: 'CUSTOM' | 'CITIZEN_REPORTS' | 'TEMPLATE' | 'HYBRID';
  isPublic: boolean;
  feedbackLink: string;
  documentUrl: string;
};

const CreateManifestoModal: React.FC<CreateManifestoModalProps> = ({
  aspirantId,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<ManifestoFormData>({
    title: '',
    content: '',
    summary: '',
    visionStatement: '',
    missionStatement: '',
    source: 'CUSTOM',
    isPublic: false,
    feedbackLink: '',
    documentUrl: ''
  });
  
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Content is required');
      return;
    }
    
    setLoading(true);
    try {
      // Payload exactly matches backend CreateManifestoRequest
      const manifestoData = {
        title: formData.title,
        content: formData.content,
        summary: formData.summary,
        visionStatement: formData.visionStatement,
        missionStatement: formData.missionStatement,
        source: 'CUSTOM',                       // enum
        isPublic: formData.isPublic,
        feedbackLink: formData.feedbackLink || null,
        documentUrl: formData.documentUrl || null,
        topIssueCategories: [],                  // optional, empty array
        sourceReportIds: []                      // optional, empty array
      };
      
      console.log('📤 Creating manifesto:', manifestoData);
      
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/create`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(manifestoData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('aspirant');
          setTimeout(() => window.location.href = '/login', 2000);
          return;
        }
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const responseData = await response.json();
      
      if (responseData.success) {
        toast.success('Manifesto created successfully!');
        window.dispatchEvent(new CustomEvent('manifesto-created'));
        onSuccess();
        onClose();
      } else {
        throw new Error(responseData.error || 'Failed to create manifesto');
      }
      
    } catch (error: any) {
      console.error('❌ Manifesto creation error:', error);
      let errorMessage = 'Failed to create manifesto';
      if (error.message) errorMessage = error.message;
      if (errorMessage.includes('401') || errorMessage.includes('Session expired')) {
        errorMessage = 'Session expired. Please login again.';
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('aspirant');
        setTimeout(() => window.location.href = '/login', 2000);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="text-primary-600 mr-3" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Create Custom Manifesto</h2>
              <p className="text-sm text-gray-600">Write your manifesto from scratch</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manifesto Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., My Vision for Ward Development"
              required
            />
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Summary *
            </label>
            <textarea
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Brief overview of your manifesto..."
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              This will be displayed in manifesto lists
            </p>
          </div>

          {/* Vision & Mission */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vision Statement
              </label>
              <textarea
                name="visionStatement"
                value={formData.visionStatement}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Your vision for the community..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mission Statement
              </label>
              <textarea
                name="missionStatement"
                value={formData.missionStatement}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="How you plan to achieve your vision..."
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manifesto Content *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={10}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
              placeholder="Write your full manifesto here..."
              required
            />
            <div className="flex items-center justify-between text-sm text-gray-500 mt-2">
              <div className="flex items-center">
                <Edit size={14} className="mr-1" />
                <span>Supports Markdown formatting</span>
              </div>
              <span>{formData.content.length} characters</span>
            </div>
          </div>

          {/* Links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document URL (Optional)
              </label>
              <input
                type="url"
                name="documentUrl"
                value={formData.documentUrl}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://example.com/manifesto.pdf"
              />
              <p className="text-sm text-gray-500 mt-1">
                Link to PDF or document version
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback Link (Optional)
              </label>
              <input
                type="url"
                name="feedbackLink"
                value={formData.feedbackLink}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://forms.google.com/feedback"
              />
              <p className="text-sm text-gray-500 mt-1">
                Where citizens can give feedback
              </p>
            </div>
          </div>

          {/* Visibility */}
          <div className="border border-gray-200 rounded-xl p-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleChange}
                className="h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
              />
              <div className="ml-3">
                <div className="font-medium text-gray-900">Make Public</div>
                <div className="text-sm text-gray-600 mt-1">
                  Citizens will be able to view this manifesto
                </div>
              </div>
            </label>
            {formData.isPublic && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center text-blue-700">
                  <Eye size={16} className="mr-2" />
                  <span className="text-sm">This manifesto will be visible to citizens in your ward</span>
                </div>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <h4 className="font-medium text-yellow-900 mb-2 flex items-center">
              <AlertCircle size={16} className="mr-2" />
              Tips for Effective Manifestos
            </h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Be specific about your commitments
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Include measurable goals
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Address local issues relevant to your ward
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Use clear, simple language
              </li>
              <li className="flex items-center">
                <CheckCircle size={14} className="text-green-500 mr-2" />
                Include timeline for implementation
              </li>
            </ul>
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.title.trim() || !formData.content.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <FileText size={18} className="mr-2" />
                  Create Manifesto
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateManifestoModal;